


import { Component, Vue } from 'vue-property-decorator';
import CompleteStep from '@/utils/mixins/completeStep';
import ConfContainer from '../../components/ConfContainer/index.vue';
import { SelectType } from '../../components/SelectionPanel/SelectionPanel.models';
import { Floor, HouseConfiguration, Module, Preset } from '../../utils/api';
import { RenderMode, HouseView } from '../../components/ConfContainer/ConfContainer.models';
import PageTransitionMode from '@/utils/mixins/pageTransitionMode';
import { namespace } from 'vuex-class';
import BackgroundVideo from '../../components/BackgroundVideo/index.vue';
import { Step } from '../../components/ProcessBar/ProcessBar.models';
import { TranslateResult } from 'vue-i18n';

const appModule = namespace('app');
const homeModule = namespace('home');
const houseSizeModule = namespace('houseSize');
const configurationModule = namespace('configuration');
const notificationModule = namespace('notify');

@Component({
  mixins: [CompleteStep, PageTransitionMode],
  components: {
    ConfContainer,
    BackgroundVideo
  }
})
export default class HouseSize extends Vue {
  @appModule.State private processRoutes!: Step[];
  @houseSizeModule.State private videoBlobUrl!: string;
  @houseSizeModule.State private videoReady!: boolean;
  @houseSizeModule.State private videoEnded!: boolean;
  @houseSizeModule.Getter private stepCompleted!: boolean;
  @configurationModule.State private houseConfiguration!: HouseConfiguration;
  @houseSizeModule.Getter private floor!: Floor;
  @homeModule.State private presets!: Preset[];

  @configurationModule.Action private selectSize!: (sizeID: string) => void;
  @notificationModule.Action private openInfoModal!: ({
    headline,
    message,
    acceptCta,
    declineCta
  }: {
    headline: TranslateResult;
    message: TranslateResult;
    acceptCta: TranslateResult;
    declineCta: TranslateResult;
  }) => Promise<void>;
  @houseSizeModule.Action private setVideoReady!: (ready: boolean) => void;
  @houseSizeModule.Action private setVideoEnded!: (ended: boolean) => void;
  @houseSizeModule.Action private preloadImages!: () => void;

  // to make ts enums work in vue template
  private SelectType: typeof SelectType = SelectType;
  private RenderMode: typeof RenderMode = RenderMode;
  private HouseView: typeof HouseView = HouseView;

  public fadeOutsideLayer = false;

  private mounted() {
    // Just simulate ready and end events if videoBlobUrl is undefined
    if (!this.videoBlobUrl) {
      this.onReady();
      this.onEnd();
    }
  }

  private onReady() {
    this.setVideoReady(true);
  }

  private onEnd() {
    this.setVideoEnded(true);
  }

  /**
   * for the house size, an outside view is rendered, and changing the
   * house size will change all the layers.
   *
   * This is hard on the browser:
   * * multiple large PNGs need to be loaded
   * * multiple large PNGs need to be rendered
   *
   * This make the view in the browser a little "jumpy". In order to alleviate this, we
   * 1) fade out the whole layer view
   * 2) preload the needed images
   * 3) fade in the layer view, with a small delay so the browser has some time to render everything
   */
  public async selectOption(option: Module) {
    const defaultCodePattern = /^B[012]0000000$/;
    const presetCodes = this.presets.map(preset => preset.baufritzCode);
    const currentCode = this.houseConfiguration.baufritzCode;

    // if we're switching away from a preset, or a "default configuration" ending with 7 0s, we're not losing anything
    if (presetCodes.includes(currentCode) || currentCode.match(defaultCodePattern)) {
      await this.changeOptionTo(option);
    } else {
      // since a change of houseSize is really loading a new configuration (and therefore, discarding
      // the old one) we need to ask the user first
      this.openInfoModal({
        headline: this.$t('global.dialogs.changeHouseSize.headline'),
        message: this.$t('global.dialogs.changeHouseSize.message'),
        declineCta: this.$t('global.dialogs.changeHouseSize.declineCta'),
        acceptCta: this.$t('global.dialogs.changeHouseSize.acceptCta')
      }).then(async () => {
        await this.changeOptionTo(option);
      });
    }
  }

  private async changeOptionTo(option: Module) {
    // first, fade out the layer and wait until the fade is complete
    this.fadeOutsideLayer = true;
    await new Promise<void>(resolve =>
      setTimeout(
        () => resolve(),
        // this needs to be the same value as in CSS
        250
      )
    );
    // then, emit the the new option ID
    this.selectSize(option.ID);
    // wait until all images have crossed the network
    await this.preloadImages();
    // finally, fade the layer in. Note that there is a slight delay in the animation in order
    // to give the browser some time to actually render the images
    this.fadeOutsideLayer = false;
  }
}
